import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaUser,
  FaComment,
  FaMapMarkerAlt,
  FaRulerHorizontal,
  FaStop,
} from "react-icons/fa";
import Map, { Marker, Source, Layer } from "react-map-gl/mapbox";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import api from "../../utils/api";
import { getCurrentLocation, reverseGeocode } from "../../utils/locationUtils";
import { getImageUrl, getProfileImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import { ROLE_IMG } from "../../utils/roleAssets";
import "./FarmerOrderTracking.css";
import "mapbox-gl/dist/mapbox-gl.css";

// Mapbox Access Token from environment variable
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Radius of the Earth in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = R * c; // Distance in kilometers
  return distance;
};

const FarmerOrderTracking = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = usePopup();
  const mapRef = useRef(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [, setUser] = useState(null);
  const [sharingLocation, setSharingLocation] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Map state
  const [viewState, setViewState] = useState(null);
  const [farmerLocation, setFarmerLocation] = useState(null);
  const [customerLocation, setCustomerLocation] = useState(null);
  const [currentLocationName, setCurrentLocationName] = useState(null); // Address from reverse geocode
  const [activeMarkerPopover, setActiveMarkerPopover] = useState(null); // 'farmer' | 'consumer' | null

  // Location tracking interval
  const locationIntervalRef = useRef(null);
  const watchPositionIdRef = useRef(null);
  const lastGeocodeTimeRef = useRef(0);

  // Calculate distance dynamically whenever locations change
  // This must be before any early returns to follow React Hooks rules
  const distance = useMemo(() => {
    if (farmerLocation && customerLocation) {
      return calculateDistance(
        farmerLocation.latitude,
        farmerLocation.longitude,
        customerLocation.latitude,
        customerLocation.longitude,
      );
    }
    return null;
  }, [farmerLocation, customerLocation]);

  // Reverse geocode farmer's current location for display (throttled to avoid Nominatim rate limits)
  useEffect(() => {
    if (!sharingLocation || !farmerLocation) {
      if (!sharingLocation) setCurrentLocationName(null);
      return;
    }
    const lat = farmerLocation.latitude;
    const lon = farmerLocation.longitude;
    const now = Date.now();
    if (now - lastGeocodeTimeRef.current < 30000) return; // Throttle: max once per 30 sec
    let cancelled = false;
    reverseGeocode(lat, lon)
      .then((addr) => {
        if (cancelled) return;
        const parts = [
          addr.address,
          addr.city,
          addr.district,
          addr.province,
        ].filter(Boolean);
        const name = parts.join(", ") || addr.displayName || "Current location";
        setCurrentLocationName(name);
        lastGeocodeTimeRef.current = Date.now(); // Only throttle after success
      })
      .catch(() => {
        if (!cancelled) setCurrentLocationName("Current location");
      });
    return () => {
      cancelled = true;
    };
  }, [sharingLocation, farmerLocation]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "farmer") {
          navigate("/");
          return;
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    } else {
      navigate("/login");
    }

    fetchOrderDetails();

    // Cleanup on unmount
    return () => {
      stopLocationSharing();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/orders/${id}`);
      const orderData = response.data;

      // Get customer location from browser
      let customerLoc = null;
      if (navigator.geolocation) {
        try {
          customerLoc = await getCurrentLocation();
          console.log("Customer location obtained:", customerLoc);
        } catch (error) {
          console.error("Error getting customer location:", error);
        }
      }

      // Get current user from localStorage for farmer info fallback
      let currentUser = null;
      try {
        const userData = localStorage.getItem("user");
        if (userData) {
          currentUser = JSON.parse(userData);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }

      // Transform order data
      const order = {
        _id: orderData._id,
        orderNumber: orderData._id.slice(-10).toUpperCase(),
        status: orderData.orderStatus || "pending",
        createdAt: new Date(orderData.createdAt),
        customer: {
          _id: orderData.consumer?._id || orderData.consumer,
          name: orderData.consumer?.name || "Customer",
          phone: orderData.consumer?.phone || "",
          email: orderData.consumer?.email || "",
          profileImage: orderData.consumer?.profileImage || null,
          username: orderData.consumer?.username || null,
          address: orderData.shippingAddress,
        },
        farmer: {
          name: orderData.farmer?.name || currentUser?.name || "Farmer",
          phone: orderData.farmer?.phone || currentUser?.phone || "",
          email: orderData.farmer?.email || currentUser?.email || "",
          profileImage:
            orderData.farmer?.profileImage || currentUser?.profileImage || null,
          username: orderData.farmer?.username || currentUser?.username || null,
          address: orderData.farmer?.address || currentUser?.address || null,
        },
        items:
          orderData.items?.map((item) => ({
            product: item.product?.name || item.name || "Product",
            quantity: item.quantity,
            price: item.price,
            image: item.product?.images?.[0]
              ? item.product.images[0].startsWith("http")
                ? item.product.images[0]
                : getImageUrl(item.product.images[0])
              : null,
          })) || [],
        totalPrice: orderData.totalPrice,
        paymentMethod: orderData.paymentMethod,
        paymentStatus: orderData.paymentStatus,
      };

      setOrder(order);

      // Set farmer location from profile if available
      if (orderData.farmer?.location) {
        const farmerLoc = {
          latitude: orderData.farmer.location.coordinates[1],
          longitude: orderData.farmer.location.coordinates[0],
        };
        setFarmerLocation(farmerLoc);
      }

      // Set customer location
      if (customerLoc) {
        setCustomerLocation({
          latitude: customerLoc.latitude,
          longitude: customerLoc.longitude,
          address: orderData.shippingAddress
            ? `${orderData.shippingAddress.street || ""}, ${orderData.shippingAddress.district || ""}`
            : "Customer location",
        });
      }

      // Set map view
      if (farmerLocation && customerLocation) {
        const centerLat =
          (farmerLocation.latitude + customerLocation.latitude) / 2;
        const centerLng =
          (farmerLocation.longitude + customerLocation.longitude) / 2;
        const distance = calculateDistance(
          farmerLocation.latitude,
          farmerLocation.longitude,
          customerLocation.latitude,
          customerLocation.longitude,
        );

        let zoom = 12;
        if (distance > 50) zoom = 9;
        else if (distance > 20) zoom = 10;
        else if (distance > 10) zoom = 11;
        else if (distance > 5) zoom = 12;
        else zoom = 13;

        setViewState({
          latitude: centerLat,
          longitude: centerLng,
          zoom: zoom,
        });
      } else if (farmerLocation) {
        setViewState({
          latitude: farmerLocation.latitude,
          longitude: farmerLocation.longitude,
          zoom: 13,
        });
      } else if (customerLocation) {
        setViewState({
          latitude: customerLocation.latitude,
          longitude: customerLocation.longitude,
          zoom: 13,
        });
      }
    } catch (error) {
      console.error("Error fetching order:", error);
    } finally {
      setLoading(false);
    }
  };

  const startLocationSharing = async () => {
    if (!navigator.geolocation) {
      setLocationError(t("farmerOrderTracking.geolocationNotSupported"));
      return;
    }

    setSharingLocation(true);
    setLocationError(null);

    // First, request permission and get initial location
    // This will trigger the browser's location permission prompt
    try {
      const location = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 15000,
            maximumAge: 0,
          },
        );
      });

      // Permission granted and location obtained successfully
      console.log("Location permission granted, location obtained:", location);
      setFarmerLocation(location);
      await updateFarmerLocation(location);

      // Update map viewState to show the map immediately
      if (customerLocation) {
        const centerLat = (location.latitude + customerLocation.latitude) / 2;
        const centerLng = (location.longitude + customerLocation.longitude) / 2;
        const distance = calculateDistance(
          location.latitude,
          location.longitude,
          customerLocation.latitude,
          customerLocation.longitude,
        );

        let zoom = 12;
        if (distance > 50) zoom = 9;
        else if (distance > 20) zoom = 10;
        else if (distance > 10) zoom = 11;
        else if (distance > 5) zoom = 12;
        else zoom = 13;

        setViewState({
          latitude: centerLat,
          longitude: centerLng,
          zoom: zoom,
        });
      } else {
        // Only farmer location, center on farmer
        setViewState({
          latitude: location.latitude,
          longitude: location.longitude,
          zoom: 13,
        });
      }

      // Now start watching position for continuous updates
      watchPositionIdRef.current = navigator.geolocation.watchPosition(
        async (position) => {
          const newLocation = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setFarmerLocation(newLocation);
          await updateFarmerLocation(newLocation);

          // Update viewState when location changes
          if (customerLocation) {
            const centerLat =
              (newLocation.latitude + customerLocation.latitude) / 2;
            const centerLng =
              (newLocation.longitude + customerLocation.longitude) / 2;
            setViewState((prev) => ({
              ...prev,
              latitude: centerLat,
              longitude: centerLng,
            }));
          } else {
            setViewState((prev) => ({
              ...prev,
              latitude: newLocation.latitude,
              longitude: newLocation.longitude,
            }));
          }
        },
        (error) => {
          console.error("Geolocation watch error:", error);
          let errorMessage;
          if (error.code === 1) {
            errorMessage = t("farmerOrderTracking.locationPermissionDenied");
          } else if (error.code === 2) {
            errorMessage = t("farmerOrderTracking.locationUnavailable");
          } else if (error.code === 3) {
            errorMessage = t("farmerOrderTracking.locationTimeout");
          } else {
            errorMessage = t("farmerOrderTracking.locationError");
          }
          setLocationError(errorMessage);
          setSharingLocation(false);
          stopLocationSharing();
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        },
      );

      // Also update location every 10 seconds as backup
      locationIntervalRef.current = setInterval(async () => {
        try {
          const backupLocation = await getCurrentLocation();
          setFarmerLocation({
            latitude: backupLocation.latitude,
            longitude: backupLocation.longitude,
          });
          await updateFarmerLocation(backupLocation);

          // Update viewState when location changes
          if (customerLocation) {
            const centerLat =
              (backupLocation.latitude + customerLocation.latitude) / 2;
            const centerLng =
              (backupLocation.longitude + customerLocation.longitude) / 2;
            setViewState((prev) => ({
              ...prev,
              latitude: centerLat,
              longitude: centerLng,
            }));
          } else {
            setViewState((prev) => ({
              ...prev,
              latitude: backupLocation.latitude,
              longitude: backupLocation.longitude,
            }));
          }
        } catch (error) {
          console.error("Error updating location:", error);
        }
      }, 10000);
    } catch (error) {
      console.error("Error getting location permission:", error);
      setSharingLocation(false);

      // Handle different error types
      let errorMessage;
      if (error.code === 1) {
        errorMessage = t("farmerOrderTracking.locationPermissionDenied");
      } else if (error.code === 2) {
        errorMessage = t("farmerOrderTracking.locationUnavailable");
      } else if (error.code === 3) {
        errorMessage = t("farmerOrderTracking.locationTimeout");
      } else {
        errorMessage = t("farmerOrderTracking.locationError");
      }

      setLocationError(errorMessage);
    }
  };

  const stopLocationSharing = async () => {
    lastGeocodeTimeRef.current = 0; // Reset so next share geocodes immediately
    if (watchPositionIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchPositionIdRef.current);
      watchPositionIdRef.current = null;
    }
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
      locationIntervalRef.current = null;
    }
    setSharingLocation(false);
    try {
      await api.put("/farmer/stop-sharing-location");
    } catch (err) {
      console.error("Error notifying stop sharing:", err);
    }
  };

  const updateFarmerLocation = async (location) => {
    try {
      await api.put("/farmer/update-location", {
        latitude: location.latitude,
        longitude: location.longitude,
      });
      console.log("Farmer location updated:", location);
    } catch (error) {
      console.error("Error updating farmer location:", error);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    setUpdatingStatus(true);
    try {
      await api.put(`/orders/${id}/status`, {
        orderStatus: newStatus,
      });
      setOrder((prev) => ({ ...prev, status: newStatus }));

      // If status is shipped, start location sharing
      if (newStatus === "shipped" && !sharingLocation) {
        startLocationSharing();
      }

      // If status is delivered, stop location sharing
      if (newStatus === "delivered") {
        stopLocationSharing();
      }
    } catch (error) {
      console.error("Error updating order status:", error);
      showToast(t("farmerOrderTracking.updateStatusFailed"), "error");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "#FF9800",
      confirmed: "#2196F3",
      processing: "#9C27B0",
      shipped: "#4CAF50",
      delivered: "#2E7D32",
      received: "#0d9488",
      cancelled: "#F44336",
    };
    return colors[status] || "#666";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t("farmerOrderTracking.pending"),
      confirmed: t("farmerOrderTracking.confirmed"),
      processing: t("farmerOrderTracking.processing"),
      shipped: t("farmerOrderTracking.shipped"),
      delivered: t("farmerOrderTracking.delivered"),
      received: toLocalizedStatus("received", t),
      cancelled: t("farmerOrderTracking.cancelled"),
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="farmer-order-tracking-page">
        <Header />
        <div className="farmer-order-tracking-loading-container">
          <div className="farmer-order-tracking-loading-spinner">
            {t("farmerOrderTracking.loading")}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="farmer-order-tracking-page">
        <Header />
        <div className="farmer-order-tracking-error-container">
          <h2>{t("farmerOrderTracking.orderNotFound")}</h2>
          <Link
            to="/farmer/orders"
            className="farmer-order-tracking-btn-primary"
          >
            {t("farmerOrderTracking.backToOrders")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="farmer-order-tracking-page">
      <Header />

      <div className="farmer-order-tracking-container">
        {/* Order Header */}
        <div className="farmer-order-tracking-order-header">
          <div>
            <h1 className="farmer-order-tracking-order-title">
              {t("farmerOrderTracking.title")}
            </h1>
            <p className="farmer-order-tracking-order-number">
              {t("farmerOrderTracking.orderNumber", {
                orderNumber: order.orderNumber,
              })}
            </p>
          </div>
          <div
            className="farmer-order-tracking-order-status-badge"
            style={{ background: getStatusColor(order.status) }}
          >
            {getStatusLabel(order.status)}
          </div>
        </div>

        {/* Location Sharing Controls */}
        <div className="farmer-order-tracking-location-sharing-section">
          <div className="farmer-order-tracking-location-sharing-header">
            <h2>{t("farmerOrderTracking.locationSharing")}</h2>
            {sharingLocation && (
              <div className="farmer-order-tracking-sharing-indicator">
                <div className="farmer-order-tracking-pulse-dot"></div>
                <span>
                  {t("farmerOrderTracking.sharingLocation", {
                    location:
                      currentLocationName ||
                      t("farmerOrderTracking.gettingLocation"),
                  })}
                </span>
              </div>
            )}
          </div>

          {locationError && (
            <div className="farmer-order-tracking-location-error-message">
              <p>{locationError}</p>
            </div>
          )}

          <div className="farmer-order-tracking-location-controls">
            {!sharingLocation ? (
              <button
                className="farmer-order-tracking-btn-start-sharing"
                onClick={startLocationSharing}
                disabled={updatingStatus}
              >
                <FaMapMarkerAlt
                  className="farmer-order-tracking-btn-icon"
                  aria-hidden
                />{" "}
                {t("farmerOrderTracking.startSharing")}
              </button>
            ) : (
              <button
                className="farmer-order-tracking-btn-stop-sharing"
                onClick={stopLocationSharing}
              >
                <FaStop
                  className="farmer-order-tracking-btn-icon"
                  aria-hidden
                />{" "}
                {t("farmerOrderTracking.stopSharing")}
              </button>
            )}
          </div>

          <p className="farmer-order-tracking-location-sharing-info">
            {sharingLocation && farmerLocation
              ? t("farmerOrderTracking.permissionGranted")
              : sharingLocation && !farmerLocation
                ? t("farmerOrderTracking.waitingPermission")
                : t("farmerOrderTracking.sharingInfo")}
          </p>
        </div>

        {/* Order Status Update */}
        <div className="farmer-order-tracking-status-update-section">
          <h2>{t("farmerOrderTracking.updateOrderStatus")}</h2>
          <div className="farmer-order-tracking-status-buttons">
            {order.status === "pending" && (
              <button
                className="farmer-order-tracking-btn-status"
                onClick={() => handleStatusUpdate("confirmed")}
                disabled={updatingStatus}
              >
                {t("farmerOrderTracking.confirmOrder")}
              </button>
            )}
            {["pending", "confirmed"].includes(order.status) && (
              <button
                className="farmer-order-tracking-btn-status"
                onClick={() => handleStatusUpdate("processing")}
                disabled={updatingStatus}
              >
                {t("farmerOrderTracking.startProcessing")}
              </button>
            )}
            {["pending", "confirmed", "processing"].includes(order.status) && (
              <button
                className="farmer-order-tracking-btn-status farmer-order-tracking-farmer-order-tracking-btn-status-primary"
                onClick={() => handleStatusUpdate("shipped")}
                disabled={updatingStatus}
              >
                {t("farmerOrderTracking.markAsShipped")}
              </button>
            )}
            {order.status === "shipped" && (
              <button
                className="farmer-order-tracking-btn-status farmer-order-tracking-farmer-order-tracking-btn-status-success"
                onClick={() => handleStatusUpdate("delivered")}
                disabled={updatingStatus}
              >
                {t("farmerOrderTracking.markAsDelivered")}
              </button>
            )}
          </div>
        </div>

        {/* Map Section */}
        {(farmerLocation || customerLocation) &&
        viewState &&
        viewState.latitude &&
        viewState.longitude ? (
          <div className="farmer-order-tracking-tracking-map-section">
            <h2 className="farmer-order-tracking-section-title">
              {t("farmerOrderTracking.deliveryMap")}
            </h2>
            {MAPBOX_TOKEN ? (
              <div className="farmer-order-tracking-tracking-map-container">
                <Map
                  ref={mapRef}
                  {...viewState}
                  onMove={(evt) => setViewState(evt.viewState)}
                  mapboxAccessToken={MAPBOX_TOKEN}
                  style={{
                    width: "100%",
                    height: "500px",
                    borderRadius: "16px",
                  }}
                  mapStyle="mapbox://styles/mapbox/streets-v12"
                >
                  {/* Line connecting farmer and customer locations */}
                  {farmerLocation && customerLocation && (
                    <Source
                      id="route-line"
                      type="geojson"
                      data={{
                        type: "Feature",
                        properties: {},
                        geometry: {
                          type: "LineString",
                          coordinates: [
                            [farmerLocation.longitude, farmerLocation.latitude],
                            [
                              customerLocation.longitude,
                              customerLocation.latitude,
                            ],
                          ],
                        },
                      }}
                    >
                      <Layer
                        id="route-line-layer"
                        type="line"
                        layout={{
                          "line-join": "round",
                          "line-cap": "round",
                        }}
                        paint={{
                          "line-color": "#4CAF50",
                          "line-width": 3,
                          "line-dasharray": [2, 2],
                        }}
                      />
                    </Source>
                  )}

                  {/* Farmer Location Marker - click to show popover */}
                  {farmerLocation && order?.farmer && (
                    <Marker
                      latitude={farmerLocation.latitude}
                      longitude={farmerLocation.longitude}
                      anchor="bottom"
                      offset={
                        farmerLocation &&
                        customerLocation &&
                        (distance !== null && distance < 0.001
                          ? [-20, 0]
                          : [0, 0])
                      }
                    >
                      <div className="farmer-order-tracking-map-marker-wrapper">
                        <div
                          className="farmer-order-tracking-farmer-marker"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMarkerPopover(
                              activeMarkerPopover === "farmer"
                                ? null
                                : "farmer",
                            );
                          }}
                        >
                          <div className="farmer-order-tracking-marker-avatar farmer-order-tracking-marker-avatar--farmer">
                            {order.farmer.profileImage ? (
                              <img
                                src={
                                  order.farmer.profileImage.startsWith("http")
                                    ? order.farmer.profileImage
                                    : getProfileImageUrl(
                                        order.farmer.profileImage,
                                      )
                                }
                                alt={order.farmer.name}
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(order.farmer.name)}&background=4CAF50&color=fff&size=64`;
                                }}
                              />
                            ) : (
                              <div className="farmer-order-tracking-marker-avatar-placeholder">
                                {order.farmer.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="farmer-order-tracking-marker-label">
                            {order.farmer.name}
                          </div>
                        </div>
                        {activeMarkerPopover === "farmer" &&
                          (() => {
                            const addr = order.farmer?.address;
                            const addressText = addr
                              ? [
                                  addr.street,
                                  addr.district,
                                  addr.city,
                                  addr.province,
                                ]
                                  .filter(Boolean)
                                  .join(", ") || "Address not set"
                              : "Address not set";
                            return (
                              <div
                                className="farmer-order-tracking-marker-popover farmer-order-tracking-marker-popover--farmer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="farmer-order-tracking-popover-name">
                                  {addressText}
                                </div>
                              </div>
                            );
                          })()}
                      </div>
                    </Marker>
                  )}

                  {/* Customer Location Marker - click to show popover */}
                  {customerLocation && order?.customer && (
                    <Marker
                      latitude={customerLocation.latitude}
                      longitude={customerLocation.longitude}
                      anchor="bottom"
                      offset={
                        distance !== null && distance < 0.01 ? [24, 0] : [0, 0]
                      }
                    >
                      <div className="farmer-order-tracking-map-marker-wrapper">
                        <div
                          className="farmer-order-tracking-customer-marker"
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveMarkerPopover(
                              activeMarkerPopover === "consumer"
                                ? null
                                : "consumer",
                            );
                          }}
                        >
                          <div className="farmer-order-tracking-marker-avatar">
                            {order.customer.profileImage ? (
                              <img
                                src={
                                  order.customer.profileImage.startsWith("http")
                                    ? order.customer.profileImage
                                    : getProfileImageUrl(
                                        order.customer.profileImage,
                                      )
                                }
                                alt={order.customer.name}
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(order.customer.name)}&background=2196F3&color=fff&size=64`;
                                }}
                              />
                            ) : (
                              <div className="farmer-order-tracking-marker-avatar-placeholder">
                                {order.customer.name.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>
                          <div className="farmer-order-tracking-marker-label">
                            {order.customer.name}
                          </div>
                        </div>
                        {activeMarkerPopover === "consumer" && (
                          <div
                            className="farmer-order-tracking-marker-popover farmer-order-tracking-marker-popover--consumer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="farmer-order-tracking-popover-name">
                              {order.customer.name}
                            </div>
                            <div className="farmer-order-tracking-popover-links">
                              <Link
                                to={`/users/${order.customer.username || order.customer._id || ""}`}
                                className="farmer-order-tracking-popover-link"
                                title="View profile"
                              >
                                <FaUser /> Profile
                              </Link>
                              <Link
                                to={`/messages?consumer=${order.customer._id || ""}`}
                                className="farmer-order-tracking-popover-link"
                                title="Message customer"
                              >
                                <FaComment /> Message
                              </Link>
                            </div>
                          </div>
                        )}
                      </div>
                    </Marker>
                  )}
                </Map>

                {/* Distance Info */}
                {distance && (
                  <div className="farmer-order-tracking-distance-info">
                    <div
                      className="farmer-order-tracking-distance-icon"
                      aria-hidden
                    >
                      <FaRulerHorizontal />
                    </div>
                    <div className="farmer-order-tracking-distance-details">
                      <span className="farmer-order-tracking-distance-label">
                        {t("farmerOrderTracking.distanceToCustomer")}
                      </span>
                      <span className="farmer-order-tracking-distance-value">
                        {distance < 1
                          ? `${toLocalizedNumber(Math.round(distance * 1000))} m`
                          : `${toLocalizedNumber(Number(distance).toFixed(2))} km`}
                      </span>
                    </div>
                  </div>
                )}

                {/* Map Legend */}
                <div className="farmer-order-tracking-map-legend">
                  {farmerLocation && (
                    <div className="farmer-order-tracking-legend-item">
                      <div className="farmer-order-tracking-legend-icon farmer">
                        <img
                          src={ROLE_IMG.farmer()}
                          alt=""
                          className="farmer-order-tracking-legend-img"
                        />
                      </div>
                      <span>{t("farmerOrderTracking.yourLocation")}</span>
                    </div>
                  )}
                  {customerLocation && (
                    <div className="farmer-order-tracking-legend-item">
                      <div className="farmer-order-tracking-legend-icon customer">
                        <img
                          src={ROLE_IMG.consumerMale()}
                          alt=""
                          className="farmer-order-tracking-legend-img"
                        />
                      </div>
                      <span>{t("farmerOrderTracking.customerLocation")}</span>
                    </div>
                  )}
                  {farmerLocation && customerLocation && (
                    <div className="farmer-order-tracking-legend-item">
                      <div className="farmer-order-tracking-legend-icon line">
                        ━━
                      </div>
                      <span>{t("farmerOrderTracking.deliveryRoute")}</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="farmer-order-tracking-map-placeholder">
                <p>{t("farmerOrderTracking.mapRequiresToken")}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="farmer-order-tracking-no-location-message">
            <p>
              {sharingLocation && !farmerLocation
                ? t("farmerOrderTracking.waitingPermission")
                : sharingLocation && farmerLocation && !viewState
                  ? t("farmerOrderTracking.settingUpMap")
                  : t("farmerOrderTracking.startSharingToSeeMap")}
            </p>
            {sharingLocation && farmerLocation && (
              <p
                style={{ color: "#4CAF50", fontSize: "14px", marginTop: "8px" }}
              >
                {t("farmerOrderTracking.locationObtained")}
              </p>
            )}
          </div>
        )}

        {/* Order Details */}
        <div className="farmer-order-tracking-order-details-section">
          <h2>{t("farmerOrderTracking.orderDetails")}</h2>
          <div className="farmer-order-tracking-order-info-grid">
            <div className="farmer-order-tracking-info-item">
              <label>{t("farmerOrderTracking.customer")}:</label>
              <Link
                to={`/users/${order.customer.username || order.customer._id || ""}`}
                className="farmer-order-tracking-customer-link"
              >
                {order.customer.name}
              </Link>
            </div>
            <div className="farmer-order-tracking-info-item">
              <label>{t("farmerOrderTracking.phone")}:</label>
              <span>{order.customer.phone || "N/A"}</span>
            </div>
            <div className="farmer-order-tracking-info-item">
              <label>{t("farmerOrderTracking.paymentMethod")}:</label>
              <span>{order.paymentMethod}</span>
            </div>
            <div className="farmer-order-tracking-info-item">
              <label>{t("farmerOrderTracking.paymentStatus")}:</label>
              <span>{toLocalizedStatus(order.paymentStatus, t)}</span>
            </div>
            <div className="farmer-order-tracking-info-item">
              <label>{t("farmerOrderTracking.totalAmount")}:</label>
              <span>Rs. {toLocalizedPrice(order.totalPrice)}</span>
            </div>
          </div>

          <div className="farmer-order-tracking-farmer-order-tracking-order-items">
            <h3>{t("farmerOrderTracking.items")}</h3>
            {order.items.map((item, index) => (
              <div key={index} className="farmer-order-tracking-order-item">
                {item.image && (
                  <img
                    src={item.image}
                    alt={item.product}
                    className="farmer-order-tracking-item-image"
                  />
                )}
                <div className="farmer-order-tracking-item-details">
                  <h4>{item.product}</h4>
                  <p>
                    {t("farmerOrderTracking.quantity", {
                      quantity: toLocalizedNumber(item.quantity),
                    })}
                  </p>
                  <p>
                    {t("farmerOrderTracking.price", {
                      price: toLocalizedPrice(item.price),
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default FarmerOrderTracking;
